// Smart auth caching for UI operations
// Provides cached auth state for UI rendering while ensuring fresh auth for security operations

import { User, Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { createLogger } from './logger';

const logger = createLogger({ module: 'authCache', level: 'debug' });

interface CachedAuthState {
  user: User | null;
  session: Session | null;
  timestamp: number;
}

class AuthStateCache {
  private cache: CachedAuthState | null = null;
  private maxAge = 5000; // 5 seconds for UI updates
  private securityMaxAge = 0; // No cache for security operations
  
  /**
   * Get user from cache or fetch fresh
   * @param options.fresh - Force fresh fetch (for security operations)
   */
  async getUser(options?: { fresh?: boolean }): Promise<User | null> {
    if (options?.fresh || !this.isValid()) {
      logger.debug('Fetching fresh user data', { fresh: options?.fresh, cacheExpired: !this.isValid() });
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        // Auth session missing is expected on unauthenticated pages, don't log as error
        if (error.name === 'AuthSessionMissingError') {
          logger.debug('No auth session found (expected on public pages)');
        } else {
          logger.error('Failed to fetch user', error);
        }
        return null;
      }
      
      // Update cache only if successful
      if (user) {
        const { data: { session } } = await supabase.auth.getSession();
        this.updateCache(user, session?.session || null);
      }
      
      return user;
    }
    
    logger.debug('Returning cached user data', { 
      age: Date.now() - this.cache!.timestamp,
      userId: this.cache!.user?.id?.substring(0, 8)
    });
    return this.cache!.user;
  }
  
  /**
   * Get session from cache or fetch fresh
   * @param options.fresh - Force fresh fetch (for security operations)
   */
  async getSession(options?: { fresh?: boolean }): Promise<Session | null> {
    if (options?.fresh || !this.isValid()) {
      logger.debug('Fetching fresh session data', { fresh: options?.fresh, cacheExpired: !this.isValid() });
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        logger.error('Failed to fetch session', error);
        return null;
      }
      
      // Update cache only if successful
      if (session) {
        const user = session.user;
        this.updateCache(user, session);
      }
      
      return session;
    }
    
    logger.debug('Returning cached session data', { 
      age: Date.now() - this.cache!.timestamp 
    });
    return this.cache!.session;
  }
  
  /**
   * Clear the cache (e.g., on logout)
   */
  clear(): void {
    logger.debug('Clearing auth cache');
    this.cache = null;
  }
  
  /**
   * Update the cache with new auth state
   */
  updateCache(user: User | null, session: Session | null): void {
    this.cache = {
      user,
      session,
      timestamp: Date.now()
    };
    logger.debug('Auth cache updated', { 
      hasUser: !!user,
      hasSession: !!session,
      userId: user?.id?.substring(0, 8)
    });
  }
  
  /**
   * Check if cache is valid
   */
  private isValid(): boolean {
    if (!this.cache) return false;
    const age = Date.now() - this.cache.timestamp;
    return age < this.maxAge;
  }
  
  /**
   * Get cache age in milliseconds
   */
  getCacheAge(): number | null {
    if (!this.cache) return null;
    return Date.now() - this.cache.timestamp;
  }
  
  /**
   * Subscribe to auth state changes to keep cache fresh
   */
  subscribeToAuthChanges(): () => void {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logger.debug('Auth state changed', { event });
      
      switch (event) {
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
        case 'USER_UPDATED':
          if (session) {
            this.updateCache(session.user, session);
          }
          break;
        
        case 'SIGNED_OUT':
          this.clear();
          break;
      }
    });
    
    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
    };
  }
}

// Export singleton instance
export const authCache = new AuthStateCache();

// Usage examples:
// 
// For UI operations (cached):
// const user = await authCache.getUser();
// 
// For security operations (always fresh):
// const user = await authCache.getUser({ fresh: true });
//
// Subscribe to changes (e.g., in App.tsx):
// useEffect(() => {
//   const unsubscribe = authCache.subscribeToAuthChanges();
//   return unsubscribe;
// }, []);